'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';
import { useCloseWhenTriggerHidden } from './useTriggerVisible';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from './ui/command';
import { Popover as ShadPopover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
/**
 * С какого числа пунктов появляется поиск.
 *
 * ЧИСЛОМ, А НЕ НА ГЛАЗ. Восемь - это список, который целиком помещается в
 * панель без прокрутки; всё, что длиннее, листают, а листать глазами дольше,
 * чем набрать две буквы. Одиннадцать языков интерфейса поиск получают, три
 * режима игры - нет.
 */
export const CSEL_SEARCH_FROM = 8;
/**
 * ПОТОЛОК ШИРИНЫ - КОНТЕЙНЕР. Правило единицы 18.
 *
 * Находка владельца: в карточке привязки поле «Любой подарок от порога»
 * уходило под границу левой колонки, и стрелка списка срезалась. Мерный
 * пролёт выше требовал ширины по тексту, `min-w-fit` делал это требование
 * ЖЁСТКИМ - и оно побеждало контейнер. Замер: поле 406 px на месте в
 * 199 px.
 *
 * Теперь ширина по тексту - пожелание, а не закон: `max-w-full` не даёт
 * полю выйти за контейнер, `min-w-0` разрешает ему сжаться, значение
 * усекается многоточием и остаётся доступным подсказкой и чтецу. Что
 * читается хуже - обрезанное значение или поле под границей, - вопрос
 * решённый: второе не читается вовсе.
 *
 * Стрелка не сжимается никогда: у триггера пресета `[&_svg]:shrink-0`, у
 * нашего - `shrink-0` явно.
 *
 * ПОЧЕМУ НЕ ПРОСТО `max-w-full`. Мерный пролёт объявлен `overflow-hidden`,
 * а у такого блока минимальная ширина нулевая: в ряду фильтров поле
 * ужималось до 55 px при 176 объявленных и резало «Все игры». Прежний
 * `min-w-fit` этого не допускал - и он же выводил поле за контейнер.
 *
 * Оба правила разом даёт `min(100%, max-content)`: не у́же своего
 * значения, пока значение помещается в контейнер, и ровно по контейнеру,
 * когда не помещается. Ряд с переносом при этом переносит поле на вторую
 * строку вместо того, чтобы молча его ужать.
 */
const CAP = 'max-w-full min-w-[min(100%,max-content)]';
/** Содержимое пункта: значок, подпись, вторая строка. */
function OptionBody({ option }) {
    return (_jsxs(_Fragment, { children: [option.iconUrl !== undefined && (_jsx("img", { src: option.iconUrl, alt: "", className: "size-5 shrink-0 rounded" })), _jsxs("span", { className: "min-w-0 flex-1", children: [_jsx("span", { className: "block truncate", children: option.label }), option.hint !== undefined && (_jsx("span", { "data-csel-hint": true, className: "text-muted-foreground block truncate text-xs", children: option.hint }))] })] }));
}
export function Csel({ id, value, options, onChange, placeholder = 'Выберите', searchPlaceholder = 'Поиск', disabled = false, triggerClassName = 'w-full', ...aria }) {
    const selected = options.find((option) => option.value === value);
    const shown = selected?.label ?? placeholder;
    const withSearch = options.length >= CSEL_SEARCH_FROM;
    /*
     * ВЫБОР БЕЗ ВАРИАНТОВ - ЗАБЛОКИРОВАН.
     *
     * Список, у которого нечего выбрать, открывался пустой панелью: в
     * «Эффектах подарков» справочник подарков приходит запросом, и до
     * ответа «Добавить подарок» раскрывался в ничто. Для человека это
     * мёртвый орган, а для сквозного сценария - гонка: он нажимал на
     * триггер раньше ответа и ждал пункт, которого ещё нет (падало на
     * каждом втором прогоне). Пустой список говорит о себе сам.
     */
    const empty = options.length === 0;
    /* Ширина триггера считается одинаково в обоих вариантах: класс места
       вызова задаёт желаемую ширину, `CAP` - потолок по контейнеру. */
    const triggerClasses = `${triggerClassName} ${CAP}`;
    if (!withSearch) {
        return (_jsx(CselSelect, { id, value, options, onChange, placeholder, triggerClasses, aria, shown, disabled: disabled || empty }));
    }
    return (_jsx(CselSearchable, { id,
        value,
        options,
        onChange,
        placeholder,
        searchPlaceholder,
        triggerClasses,
        aria, disabled: disabled || empty }));
}
/**
 * ПУСТАЯ СТРОКА - НЕ ЗНАЧЕНИЕ ДЛЯ RADIX, А ПРИЗНАК «НИЧЕГО НЕ ВЫБРАНО».
 *
 * `Select` пресета показывает placeholder всякий раз, когда значение
 * равно `''`, и детей поля не рисует вовсе. У продукта же пустая строка -
 * это нормальный пункт: «Все игры», «Все аккаунты», «Любой подарок».
 * Фильтр аккаунтов в аналитике так и показывал «Выберите» вместо «Все
 * аккаунты», а сквозной сценарий этого не замечал: он искал текст в
 * `textContent`, где его подставлял невидимый мерный пролёт. Пролёт
 * сняли - дефект стал виден.
 *
 * Пустая строка подменяется служебным значением на границе с библиотекой
 * и возвращается обратно сразу за ней: наружу компонент по-прежнему
 * говорит на языке продукта.
 */
const EMPTY = '\u0000efira-empty';
const toRadix = (value) => (value === '' ? EMPTY : value);
const fromRadix = (value) => (value === EMPTY ? '' : value);
/** Короткий список: выпадающий выбор без поиска. */
function CselSelect({ id, value, options, onChange, placeholder, disabled, triggerClasses, aria, shown, }) {
    const selected = options.find((option) => option.value === value);
    const [open, setOpen] = useState(false);
    const trigger = useRef(null);
    const close = useCallback(() => {
        setOpen(false);
    }, []);
    useCloseWhenTriggerHidden(trigger, open, close);
    return (_jsxs(Select, { value: toRadix(value), disabled: disabled, open: open, onOpenChange: setOpen, onValueChange: (next) => {
            onChange(fromRadix(next));
        }, children: [_jsx(SelectTrigger, { ref: trigger, id: id, "data-csel-trigger": true, title: shown, className: triggerClasses, ...aria, children: _jsx(SelectValue, { placeholder: placeholder, className: "min-w-0 flex-1", children: _jsx("span", { className: "truncate", children: selected?.label ?? placeholder }) }) }), _jsx(SelectContent, { "data-csel-panel": true, position: "popper", align: "start", sideOffset: 4, children: options.map((option) => (_jsx(SelectItem, { value: toRadix(option.value), disabled: option.disabled === true, title: option.reason, "data-csel-option": option.value, children: _jsx("span", { className: "flex w-full items-center gap-2", children: _jsx(OptionBody, { option: option }) }) }, option.value))) })] }));
}
/** Длинный список: то же поле, но с поиском (Combobox). */
function CselSearchable({ id, value, options, onChange, placeholder, searchPlaceholder, disabled, triggerClasses, aria, }) {
    const [open, setOpen] = useState(false);
    const trigger = useRef(null);
    const close = useCallback(() => {
        setOpen(false);
    }, []);
    useCloseWhenTriggerHidden(trigger, open, close);
    const selected = useMemo(() => options.find((option) => option.value === value), [options, value]);
    const shown = selected?.label ?? placeholder;
    return (_jsxs(ShadPopover, { open: open, onOpenChange: setOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { ref: trigger, id: id, type: "button", "data-csel-trigger": true, role: "combobox", title: shown, "aria-expanded": open, disabled: disabled, className: `border-input bg-background text-foreground flex h-9 items-center justify-between gap-2 rounded-md border px-3 py-1 text-sm shadow-xs disabled:opacity-50 ${triggerClasses}`, ...aria, children: [_jsx("span", { className: "min-w-0 flex-1 truncate text-start", children: selected === undefined ? (_jsx("span", { className: "text-muted-foreground", children: placeholder })) : (selected.label) }), _jsx(Icon, { name: "chevronDown", size: "sm", className: `shrink-0 ${open ? 'rotate-180' : ''}` })] }) }), _jsx(PopoverContent, { "data-csel-panel": true, align: "start", className: "w-(--radix-popover-trigger-width) min-w-56 p-0", children: _jsxs(Command, { children: [_jsx(CommandInput, { "data-csel-search": true, placeholder: searchPlaceholder }), _jsxs(CommandList, { children: [_jsx(CommandEmpty, { children: "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C. \u0423\u0442\u043E\u0447\u043D\u0438\u0442\u0435 \u0437\u0430\u043F\u0440\u043E\u0441." }), _jsx(CommandGroup, { children: options.map((option) => (_jsxs(CommandItem, { value: `${option.label} ${option.value}`, disabled: option.disabled === true, title: option.reason, "data-csel-option": option.value, onSelect: () => {
                                            onChange(option.value);
                                            setOpen(false);
                                        }, children: [_jsx(OptionBody, { option: option }), option.value === value && (_jsx(Icon, { name: "success", size: "sm", className: "text-primary ms-auto shrink-0" }))] }, option.value))) })] })] }) })] }));
}
//# sourceMappingURL=Csel.js.map